import { useReducer } from 'react';

import { useIsFetching, useQueryClient } from '@tanstack/react-query';

import { useBreadBoard } from 'components/contexts/Toaster';
import useAddablePersonnelCounts from 'components/projectPersonnel/components/assignPersonnelSidePanel/hooks/useAddablePersonnelCounts';
import selectionsReducer, {
  initialSelectionsState,
} from 'components/projectPersonnel/components/assignPersonnelSidePanel/reducers/selectionsReducer';
import { useProjectPersonnelContext } from 'components/projectPersonnel/contexts/ProjectPersonnelContext';

const RESOURCE_PATH_SEGMENTS = {
  roleIds: 'company_roles',
  teamIds: 'teams',
};

export default function useSelections() {
  const { addableCounts, fetchAddableCounts } = useAddablePersonnelCounts();
  const { addInedibleToast } = useBreadBoard();
  const { projectId } = useProjectPersonnelContext();
  const queryClient = useQueryClient();

  const [state, dispatch] = useReducer(
    selectionsReducer,
    undefined,
    initialSelectionsState,
  );

  const isComputingPersonnelSelections =
    useIsFetching({
      queryKey: ['projects', projectId],
      predicate: ({ queryKey }) =>
        queryKey.includes('unassignedAssignablePersonnelSearches'),
    }) > 0;

  const isComputingRoleSelections =
    useIsFetching({
      queryKey: ['projects', projectId],
      predicate: ({ queryKey }) =>
        (queryKey.includes('companyRoleSearches') &&
          queryKey.at(-1)?.page === 'all') ||
        queryKey.includes('unassignedAssignablePersonnelSearches'),
    }) > 0;

  const isComputingTeamSelections =
    useIsFetching({
      queryKey: ['projects', projectId],
      predicate: ({ queryKey }) =>
        (queryKey.includes('teamSearches') &&
          queryKey.at(-1)?.page === 'all') ||
        queryKey.includes('unassignedAssignablePersonnelSearches'),
    }) > 0;

  const fetchAddableCountsForResources = async ({ roleIds, teamIds }) => {
    await Promise.allSettled([
      fetchAddableCounts({ resourceIds: roleIds, resourceType: 'roleIds' }),
      fetchAddableCounts({ resourceIds: teamIds, resourceType: 'teamIds' }),
    ]);

    dispatch({
      type: 'RESOURCE_ADDABLE_COUNTS_FETCHED',
      payload: {
        addableCounts: {
          roleIds: addableCounts({
            resourceIds: roleIds,
            resourceType: 'roleIds',
          }),
          teamIds: addableCounts({
            resourceIds: teamIds,
            resourceType: 'teamIds',
          }),
        },
      },
    });
  };

  const selectUnassignedAssignablePersonnel = async ({
    resourceId,
    resourceType,
  }) => {
    try {
      const { data } = await queryClient.fetchQuery({
        queryKey: [
          'projects',
          projectId,
          RESOURCE_PATH_SEGMENTS[resourceType],
          resourceId,
          'unassignedAssignablePersonnelSearches',
        ],
        queryFn: async () => {
          const response = await axios.post(
            `/projects/${projectId}/${RESOURCE_PATH_SEGMENTS[resourceType]}/${resourceId}/unassigned_assignable_personnel_searches`,
          );

          return response.data;
        },
      });

      const personnel = data.map(
        ({
          id: personnelId,
          relationships: {
            companyRoles: { data: companyRolesData } = { data: [] },
            teams: { data: teamsData } = { data: [] },
          },
        }) => ({
          personnelId,
          roleIds: companyRolesData.map(({ id }) => id),
          teamIds: teamsData.map(({ id }) => id),
        }),
      );

      dispatch({
        type: 'PERSONNEL_SELECTED',
        payload: { personnel, resource: { resourceId, resourceType } },
      });

      await fetchAddableCountsForResources({
        roleIds: personnel.flatMap(({ roleIds }) => roleIds),
        teamIds: personnel.flatMap(({ teamIds }) => teamIds),
      });
    } catch {
      addInedibleToast();
    }
  };

  const handleResourceCheckboxChange = ({ resourceId, resourceType }) => {
    if (state[resourceType].has(resourceId)) {
      dispatch({
        type: 'RESOURCE_DESELECTED',
        payload: { resourceId, resourceType },
      });
    } else {
      dispatch({
        type: 'RESOURCE_SELECTED',
        payload: { resourceId, resourceType },
      });

      selectUnassignedAssignablePersonnel({ resourceId, resourceType });
    }
  };

  const handlePersonnelCheckboxChange = async ({
    personnelId,
    roleIds,
    teamIds,
  }) => {
    if (state.personnel.has(personnelId)) {
      dispatch({ type: 'PERSONNEL_DESELECTED', payload: { personnelId } });
    } else {
      dispatch({
        type: 'PERSONNEL_SELECTED',
        payload: { personnel: [{ personnelId, roleIds, teamIds }] },
      });

      await fetchAddableCountsForResources({ roleIds, teamIds });
    }
  };

  const handleProjectPersonnelTaken = ({ personnelIds }) => {
    dispatch({ type: 'PROJECT_PERSONNEL_TAKEN', payload: { personnelIds } });
  };

  const handleRoleCheckboxChange = ({ roleId }) => {
    handleResourceCheckboxChange({
      resourceId: roleId,
      resourceType: 'roleIds',
    });
  };

  const handleTeamCheckboxChange = ({ teamId }) => {
    handleResourceCheckboxChange({
      resourceId: teamId,
      resourceType: 'teamIds',
    });
  };

  const handleSelectionsReset = () => dispatch({ type: 'SELECTIONS_RESET' });

  return {
    handlePersonnelCheckboxChange,
    handleProjectPersonnelTaken,
    handleRoleCheckboxChange,
    handleSelectionsReset,
    handleTeamCheckboxChange,
    isComputingPersonnelSelections,
    isComputingRoleSelections,
    isComputingTeamSelections,
    selections: state,
  };
}
